import { IAction, ISuccessResponse } from '@Root/types'
import { put, takeLatest, select } from 'redux-saga/effects'
import * as Eff from 'redux-saga/effects' // tslint:disable-line:no-duplicate-imports
import toastr from 'toastr'
import router from '@Site/Router'
import showConfetti from '@Utils/ShowConfettiAnimation'

import Logger from '@Utils/Logger'

import {
  fetchProjectVersionSettingsSuccess,
  projectVersionSettingsFailure,
  submitProjectVersionUpdateSuccess,
  submitProjectVersionUpdateFailure,
  updateDownloadingElementsSuccess,
  updateDownloadingElementsFailure,
  fetchTranslationLanguagesSuccess,
  fetchTranslationLanguagesFailure,
} from './actions'

import {
  ProjectVersionSettingsActionTypes,
  ISubmitProjectVersionUpdatePayload,
  IFetchProjectVersionSettingsSuccessResponseBody,
  IFetchProjectVersionSettingsPayload,
  IUpdateDownloadingElementsPayload,
  IUpdateDownloadingElementsSuccessResponseBody,
  IFetchTranslationLanguagesPayload,
  IFetchTranslationLanguagesSuccessResponseBody,
} from './types'

import {
  getUserInfo,
} from '@SiteModules/User/actions'

import {
  fetchElements,
} from '@SiteModules/Element/actions'

import {
  fetchProjects,
} from '@SiteModules/Project/actions'

import {
  closeSetupProjectPopup,
} from '@SiteModules/Popup/actions'

import * as API from './api'

const call: any = Eff.call

function* submitProjectVersionUpdateSaga(action: IAction<ISubmitProjectVersionUpdatePayload>): Generator<any, any, any> {
  const payload = action.payload

  try {
    yield call(API.submitProjectVersionUpdate, payload)
    yield put(submitProjectVersionUpdateSuccess())

    if (payload.includesTranscript) {
      toastr.info('You\'ll get an email once it\'s ready ✉️', 'We\'re processing your project(s)')
    } else {
      toastr.success('', 'Request submitted')
    }

    const isForOneProjectVersion = payload.projectVersionIds.length === 1

    if (isForOneProjectVersion && payload.includesTranscript) {
      router.navigate(`/processing?projectVersionId=${payload.projectVersionIds[0]}`)
      yield put(closeSetupProjectPopup())
    } else {
      yield put(closeSetupProjectPopup())
      showConfetti()
    }

    const state = yield select()
    yield put(getUserInfo())

    const payloadFetchElements = {
      workspaceId: payload.workspaceId,
      page: 1
    }

    if (state.elements.hasLoadedInitially) {
      yield put(fetchElements(payloadFetchElements))
    }

    const searchText = state.projects.searchText
    const payloadFetchProjects = {
      ...payloadFetchElements,
      selectedLabels: state.labels.selectedLabelIds,
      ...(searchText.length > 0 ? { searchText } : {}),
    }

    yield put(fetchProjects(payloadFetchProjects))
  } catch (e) {
    yield put(submitProjectVersionUpdateFailure())
    Logger.error(e, 'Request could not be submitted')
  }
}

function* fetchProjectVersionSettingsSaga(action: IAction<IFetchProjectVersionSettingsPayload>) {
  try {
    const response: ISuccessResponse<IFetchProjectVersionSettingsSuccessResponseBody> = yield call(API.fetchProjectVersionSettings, action.payload)

    yield put(fetchProjectVersionSettingsSuccess(response.data))
  } catch (e) {
    Logger.error(e, 'Settings could not be loaded')
    yield put(projectVersionSettingsFailure())
  }
}

function* updateDownloadingElementsSaga(action: IAction<IUpdateDownloadingElementsPayload>) {
  try {
    const response: ISuccessResponse<IUpdateDownloadingElementsSuccessResponseBody> = yield call(API.updateDownloadingElements, action.payload)

    yield put(updateDownloadingElementsSuccess(response.data))
  } catch (e) {
    Logger.error(e, 'Downloading elements could not be updated')
    yield put(updateDownloadingElementsFailure())
  }
}

function* fetchTranslationLanguagesSaga(action: IAction<IFetchTranslationLanguagesPayload>) {
  try {
    const response: ISuccessResponse<IFetchTranslationLanguagesSuccessResponseBody> = yield call(API.fetchTranslationLanguages, action.payload)

    yield put(fetchTranslationLanguagesSuccess(response.data))
  } catch (e) {
    Logger.error(e, 'Translation languages could not be loaded')
    yield put(fetchTranslationLanguagesFailure())
  }
}

function* projectVersionSettingsSagas() {
  yield takeLatest(ProjectVersionSettingsActionTypes.SUBMIT_PROJECT_VERSION_UPDATE, submitProjectVersionUpdateSaga)
  yield takeLatest(ProjectVersionSettingsActionTypes.FETCH_PROJECT_VERSION_SETTINGS, fetchProjectVersionSettingsSaga)
  yield takeLatest(ProjectVersionSettingsActionTypes.UPDATE_DOWNLOADING_ELEMENTS, updateDownloadingElementsSaga)
  yield takeLatest(ProjectVersionSettingsActionTypes.FETCH_TRANSLATION_LANGUAGES, fetchTranslationLanguagesSaga)
}

export default projectVersionSettingsSagas
